import React from 'react';
import dayjs from 'dayjs';

import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  InputAdornment,
  List,
  ListItemButton,
  TextField,
  Typography,
} from '@mui/material';
import { BiCalendar, BiCar, BiMap, BiSearch } from 'react-icons/bi';

import { Order_By } from '@gql/base-types';
import { GetMobileServiceAppointmentsQuery, useGetMobileServiceAppointmentsQuery } from '../queries/getMobileServiceAppointments.query.generated';

// ── Types ------------------------------------------------------------------ //

export type PickableAppointment = GetMobileServiceAppointmentsQuery['appointments'][number];

interface AddStopPickerDialogProps {
  open: boolean;
  onClose: () => void;
  customerId: number | null;
  /** Appointments already staged in the draft (hidden from the picker). */
  excludeAppointmentIds: number[];
  onPick: (appointment: PickableAppointment) => void;
}

// ── Component --------------------------------------------------------------- //

/**
 * Pending-appointment picker for run edit mode (phase 3).
 *
 * Stops are born from appointments (see docs/run-edit-mode-design.md), so
 * adding a stop means picking an appointment that isn't on a run yet — the
 * same availability rule BuildRunDialog uses.
 */
const AddStopPickerDialog: React.FC<AddStopPickerDialogProps> = ({
  open,
  onClose,
  customerId,
  excludeAppointmentIds,
  onPick,
}) => {
  const [search, setSearch] = React.useState('');

  React.useEffect(() => {
    if (open) setSearch('');
  }, [open]);

  const dateRange = React.useMemo(() => ({
    start: dayjs().subtract(90, 'day').startOf('day').toISOString(),
    end: dayjs().add(30, 'day').endOf('day').toISOString(),
  }), []);

  const { data, loading } = useGetMobileServiceAppointmentsQuery({
    variables: {
      customerId: customerId as number,
      start: dateRange.start,
      end: dateRange.end,
      limit: 200,
      offset: 0,
      orderBy: [{ appointment_time: Order_By.DescNullsLast }, { id: Order_By.DescNullsLast }],
      typeFilter: { type: { _eq: 'mobile_service' } },
    },
    fetchPolicy: 'cache-and-network',
    skip: !customerId || !open,
  });

  const available = React.useMemo(() => {
    const excluded = new Set(excludeAppointmentIds);
    const term = search.trim().toLowerCase();
    return (data?.appointments ?? [])
      .filter(a => !excluded.has(a.id))
      // Not on a run yet — the queue-availability rule.
      .filter(a => (a.service_orders ?? []).every(o => !o.stop))
      .filter(a => {
        if (!term) return true;
        const haystack = [
          a.consumer_name,
          a.pickup?.address,
          a.vehicle_make,
          a.vehicle_model,
          a.ro_number,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(term);
      })
      // Chronological (no-time last) — soonest appointments first when
      // adding to an active route.
      .sort((a, b) =>
        (a.appointment_time ?? '9999').localeCompare(b.appointment_time ?? '9999')
      );
  }, [data, excludeAppointmentIds, search]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <Box sx={{ px: 2.5, pt: 2.25, pb: 1.75, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant='h6' sx={{ fontWeight: 600, mb: 0.5 }}>
          Add stop
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
          Pick a pending appointment to add to this run.
        </Typography>
        <TextField
          size='small'
          fullWidth
          placeholder='Search by name, address, vehicle, or RO…'
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <BiSearch size={16} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <DialogContent sx={{ px: 1.25, py: 1, minHeight: 200, maxHeight: 420 }}>
        {loading && available.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={22} />
          </Box>
        ) : available.length === 0 ? (
          <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center', py: 4 }}>
            {search ? 'No pending appointments match your search.' : 'No pending appointments to add.'}
          </Typography>
        ) : (
          <List dense disablePadding>
            {available.map(appt => {
              const vehicle = [appt.vehicle_year, appt.vehicle_make, appt.vehicle_model].filter(Boolean).join(' ');
              return (
                <ListItemButton
                  key={appt.id}
                  onClick={() => onPick(appt)}
                  sx={{ borderRadius: 1.5, alignItems: 'flex-start', py: 1 }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                      {appt.consumer_name || 'Unknown customer'}
                      {appt.ro_number ? (
                        <Typography component='span' variant='caption' color='text.secondary' sx={{ ml: 1 }}>
                          RO #{appt.ro_number}
                        </Typography>
                      ) : null}
                    </Typography>
                    {appt.pickup?.address ? (
                      <Typography variant='caption' color='text.secondary' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <BiMap size={12} style={{ flexShrink: 0 }} />
                        {appt.pickup.address}
                      </Typography>
                    ) : (
                      <Typography variant='caption' color='warning.main' sx={{ display: 'block' }}>
                        No service address on the appointment
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.25 }}>
                      {vehicle ? (
                        <Typography variant='caption' color='text.secondary' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <BiCar size={12} style={{ flexShrink: 0 }} />
                          {vehicle}
                        </Typography>
                      ) : null}
                      {appt.appointment_time ? (
                        <Typography variant='caption' color='text.secondary' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <BiCalendar size={12} style={{ flexShrink: 0 }} />
                          {dayjs(appt.appointment_time).format('MMM D, h:mm A')}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddStopPickerDialog;
